import { Injectable } from '@angular/core';
import { CommunicationService } from '../../http/communication.service';
import { BehaviorSubject, catchError, Observable } from 'rxjs';
import { Account, AccountPayload, AccountSummary, AccountUsersResponse, AiTopicsResponse, BrandLogoResponse, BrandPayload, BrandsResponse, BrandSummary, CheckDomainConflictsPayload, DomainConflictsResponse, ManagementRequest, MetaPageRecord, MetaPageValidationResult, Topic, UserPayload, UserResponse } from './types';
import { User } from '@auth0/auth0-angular';
import { AdvertiserDomainsResponse } from '../../services/types';

@Injectable({
  providedIn: 'root'
})
export class ManagementService {

  private _brandsList: BrandSummary[] = [];
  private _userRoles: string[] = [];
  private _accountList: AccountSummary[] = [];
  private _aiTopics: Topic[] = [];
  private _brandsListSubject = new BehaviorSubject<BrandSummary[]>([]);
  public brandsList$ = this._brandsListSubject.asObservable();

  constructor(private communicationService: CommunicationService) { }

  get brandsList(): BrandSummary[] {
    return this._brandsList;
  }
  set brandsList(brands: BrandSummary[]) {
    this._brandsList = brands;
    this._brandsListSubject.next(brands);
  }

  get userRoles(): string[] {
    return this._userRoles;
  }
  set userRoles(roles: string[]) {
    this._userRoles = roles;
  }

  get accountList(): AccountSummary[] {
    return this._accountList;
  }
  set accountList(accounts: AccountSummary[]) {
    this._accountList = accounts;
  }

  get aiTopics(): Topic[] {
    return this._aiTopics;
  }
  set aiTopics(topics: Topic[]) {
    this._aiTopics = topics;
  }

  updateBrandInList(updatedBrand: BrandSummary): void {
    const index = this._brandsList.findIndex(
      brand => brand.brandKey === updatedBrand.brandKey
    );

    if (index !== -1) {
      const updatedList = [...this._brandsList];
      updatedList[index] = { ...updatedList[index], ...updatedBrand };
      this.brandsList = updatedList;
    }
  }

  public getUserRoles(): Observable<{ roles: string[] }> {
    return this.communicationService.httpGet<{ roles: string[] }>('/users-dashboard/constants').pipe(
      catchError((error) => {
        console.error('Error fetching user roles', error);
        throw error;
      }
      ));
  }

  public getUsers(params: Partial<ManagementRequest>): Observable<UserResponse> {
    return this.communicationService.httpGet<UserResponse>('/users/paginated', { params }).pipe(
      catchError((error) => {
        console.error('Error fetching users', error);
        throw error;
      }
      ));
  }

  public addUser(payload: UserPayload): Observable<User> {
    return this.communicationService.httpPost<User>('/users', payload).pipe(
      catchError((error) => {
        console.error('Error adding user', error);
        throw error;
      }
      ));
  }

  public editUser(payload: UserPayload): Observable<User> {
    return this.communicationService.httpPut<User>(`/users/${payload.id}`, payload).pipe(
      catchError((error) => {
        console.error('Error editing user', error);
        throw error;
      }
      ));
  }

  public deleteUser(userId: string): Observable<User> {
    return this.communicationService.httpDelete<User>(`/users/${userId}`).pipe(
      catchError((error) => {
        console.error('Error deleting user', error);
        throw error;
      }
      ));
  }

  public getBrands(params: ManagementRequest): Observable<BrandsResponse> {
    return this.communicationService.httpGet<BrandsResponse>('/brands', { params }).pipe(
      catchError((error) => {
        console.error('Error fetching brands', error);
        throw error;
      }
      ));
  }

  public getAccounts(params: Partial<ManagementRequest>): Observable<{ accounts: Account[]; total: number }> {
    return this.communicationService.httpGet<{ accounts: Account[]; total: number }>('/accounts', { params }).pipe(
      catchError((error) => {
        console.error('Error fetching accounts', error);
        throw error;
      }
      ));
  }

  public addAccount(payload: AccountPayload): Observable<Account> {
    return this.communicationService.httpPost<Account>('/accounts', payload).pipe(
      catchError((error) => {
        console.error('Error adding account', error);
        throw error;
      }
      ));
  }

  public editAccount(payload: AccountPayload): Observable<Account> {
    return this.communicationService.httpPut<Account>(`/accounts/${payload.accountKey}`, payload).pipe(
      catchError((error) => {
        console.error('Error editing account', error);
        throw error;
      }
      ));
  }

  public deleteAccount(accountKey: string): Observable<Account> {
    return this.communicationService.httpDelete<Account>(`/accounts/${accountKey}`).pipe(
      catchError((error) => {
        console.error('Error deleting account', error);
        throw error;
      }
      ));
  }

  public getAccountUsers(accountKey: string): Observable<AccountUsersResponse> {
    return this.communicationService.httpGet<AccountUsersResponse>(`/accounts/users/${accountKey}`,).pipe(
      catchError((error) => {
        console.error('Error fetching account users', error);
        throw error;
      }
      ));
  }

  public searchAdvertisersDomains(query: string): Observable<AdvertiserDomainsResponse> {
    return this.communicationService.httpGet<AdvertiserDomainsResponse>(`/advertiser-domains/search/${query}`,).pipe(
      catchError((error) => {
        console.error('Error searching advertiser domains', error);
        throw error;
      }
      ));
  }

  public checkDomainConflicts(payload: CheckDomainConflictsPayload): Observable<DomainConflictsResponse> {
    return this.communicationService.httpPost<DomainConflictsResponse>('/brands/check-domain-conflicts', payload).pipe(
      catchError((error) => {
        console.error('Error checking domain conflicts', error);
        throw error;
      })
    );
  }

  public addBrand(payload: BrandPayload): Observable<BrandsResponse> {
    return this.communicationService.httpPost<BrandsResponse>('/brands', payload).pipe(
      catchError((error) => {
        console.error('Error adding brand', error);
        throw error;
      }
      ));
  }

  public editBrand(payload: BrandPayload): Observable<BrandsResponse> {
    return this.communicationService.httpPut<BrandsResponse>(`/brands/${payload.brandKey}`, payload).pipe(
      catchError((error) => {
        console.error('Error editing brand', error);
        throw error;
      }
      ));
  }

  public validateMetaPage(pageId: string, countries: string[]): Observable<MetaPageValidationResult> {
    return this.communicationService.httpPost<MetaPageValidationResult>('/brands/meta-pages/validate', { pageId, countries }).pipe(
      catchError((error) => {
        console.error('Error validating meta page', error);
        throw error;
      }
      ));
  }

  public getBrandMetaPages(brandKey: string): Observable<MetaPageRecord[]> {
    return this.communicationService.httpGet<MetaPageRecord[]>(`/brands/${brandKey}/meta-pages`).pipe(
      catchError((error) => {
        console.error('Error fetching brand meta pages', error);
        throw error;
      }
      ));
  }

  public getMetaPageCountries(): Observable<{ code: string; name: string }[]> {
    return this.communicationService.httpGet<{ code: string; name: string }[]>('/brands/meta-pages/countries').pipe(
      catchError((error) => {
        console.error('Error fetching meta page countries', error);
        throw error;
      }
      ));
  }

  public fetchAndStoreBrands(params: ManagementRequest = {}): void {
    this.getBrands(params).subscribe({
      next: (res) => {
        this.brandsList = res.brands;
      },
      error: (err) => {
        console.error('Error refreshing brands list', err);
      }
    });
  }

  public fetchAndStoreAccounts(params: ManagementRequest = {}): void {
    this.getAccounts(params).subscribe({
      next: (res) => {
        this.accountList = res.accounts;
      },
      error: (err) => {
        console.error('Error refreshing brands list', err);
      }
    });
  }

  public findBrandLogo(brandName: string): Observable<BrandLogoResponse> {
    return this.communicationService.httpPost<BrandLogoResponse>('/brands/logo/find', { brandName }).pipe(
      catchError((error) => {
        console.error('Error finding brand logo', error);
        throw error;
      })
    );
  }

  public uploadBrandLogo(brandName: string, logoFile: File): Observable<BrandLogoResponse> {
    const formData = new FormData();
    formData.append('file', logoFile);
    formData.append('brandName', brandName);

    return this.communicationService.httpPost<BrandLogoResponse>(`/brands/logo/upload`, formData).pipe(
      catchError((error) => {
        console.error('Error uploading brand logo', error);
        throw error;
      })
    );
  }

  public getAiTopics(): Observable<AiTopicsResponse> {
    return this.communicationService.httpGet<AiTopicsResponse>('/ai-topics').pipe(
      catchError((error) => {
        console.error('Error fetching ai topics', error);
        throw error;
      })
    );
  }
}
